import {
	FC,
	HTMLAttributes,
	KeyboardEvent,
	useEffect,
	useRef,
	useState,
} from 'react';
import clx from 'classnames';
import { useDebounce, useOutsideClick, useToggle } from 'src/Hooks';
import API from 'src/API';
import { CustomerItemType } from 'src/Types';
import ClearBtn from '../../Components/ClearBtn/ClearBtn';
import { ReactComponent as SearchIcon } from './Search.svg';
import st from './ParticipantSelect.module.scss';
import UserInfo from '../../Components/UserInfo/UserInfo';

interface IParticipantSelectProps extends HTMLAttributes<HTMLDivElement> {
	defaultOpen?: boolean;
	placeholder?: string;
	showClearBtn?: boolean;
	minSearchLength?: number;
	// Размер страницы для подгрузки списка при прокрутке
	paginationPageSize?: number;
	disabled?: boolean;
	value?: string;
	setValue: (value?: CustomerItemType) => void;
	// Сообщает о найденном пользователе при восстановлении выбора по хэшу
	onResolve?: (value: CustomerItemType) => void;
	// Сообщает об ошибке восстановления выбранного пользователя по хэшу
	onLookupFailure?: () => void;
	// Сохраняет заданный хэш, если пользователя временно не удалось загрузить
	preserveValueOnLookupFailure?: boolean;
	onOutsideClick?: (e: any) => void;
}

export const ParticipantSelect: FC<IParticipantSelectProps> = (props) => {
	const {
		value,
		setValue,
		onResolve,
		onLookupFailure,
		preserveValueOnLookupFailure = false,
		defaultOpen = false,
		showClearBtn = false,
		minSearchLength = 3,
		paginationPageSize,
		placeholder = '',
		disabled = false,
		className,
		onOutsideClick,
		...otherProps
	} = props;
	// Без value компонент хранит выбранного пользователя только локально
	const hasControlledValue = Object.prototype.hasOwnProperty.call(
		props,
		'value',
	);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const modalRef = useOutsideClick<HTMLDivElement>(
		(e) => {
			if (onOutsideClick) {
				onOutsideClick(e);
			} else if (isOpened) {
				toggle();
			}
		},
		undefined,
		wrapperRef,
	);
	const [isOpened, toggle] = useToggle(defaultOpen);
	const [isWait, setIsWait] = useState<boolean>(false);
	const [data, setData] = useState<CustomerItemType[]>([]);
	const [selected, setSelected] = useState<CustomerItemType>();
	const [search, setSearch] = useDebounce<string>(onSearch, '');
	const [focusedIndex, setFocusedIndex] = useState(-1);
	// Положение в постраничном списке используется только при paginationPageSize
	const [page, setPage] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	// Не даём старому ответу поиска заменить результаты нового запроса
	const listRequestRef = useRef(0);
	const pageLoadingRef = useRef(false);
	// Асинхронный ответ по хэшу не должен вернуть уже очищенное значение
	const currentValueRef = useRef(value);
	currentValueRef.current = value;
	const selectedName =
		selected && (!hasControlledValue || selected.id === value)
			? selected.text
			: '';

	useEffect(() => {
		if (isOpened) {
			if (minSearchLength === -1 || minSearchLength <= search.length) {
				loadList(paginationPageSize != null ? search : undefined);
			}
			inputRef.current?.focus();
		}
		// eslint-disable-next-line
	}, [isOpened]);

	useEffect(() => {
		if (value) {
			onSetSelectedInfo(data);
		} else {
			setSelected(undefined);
		}
		// eslint-disable-next-line
	}, [value]);

	function onSearch(search: any) {
		if (
			!!isOpened &&
			!!search &&
			(minSearchLength <= 0 || search.length >= minSearchLength)
		) {
			loadList(search);
		}
	}

	// Загружает первую страницу поиска или дополняет список следующей
	const loadList = (search?: string, requestedPage = 0) => {
		if (requestedPage > 0 && pageLoadingRef.current) {
			return;
		}
		const requestId = ++listRequestRef.current;
		pageLoadingRef.current = true;
		setIsWait(true);
		API.getParticipants(search, requestedPage, paginationPageSize ?? 5)
			.then((result) => {
				if (requestId !== listRequestRef.current) {
					return;
				}
				setData((current) => {
					return requestedPage === 0
						? result
						: [...current, ...result];
				});
				setPage(requestedPage);
				setHasMore(
					paginationPageSize != null &&
						result.length === paginationPageSize,
				);
				if (requestedPage === 0) {
					setFocusedIndex(-1);
					onSetSelectedInfo(result);
				}
			})
			.catch((e: any) => {
				console.warn(e);
			})
			.finally(() => {
				if (requestId === listRequestRef.current) {
					pageLoadingRef.current = false;
					setIsWait(false);
				}
			});
	};

	const onSetSelectedInfo = (list: Array<CustomerItemType>) => {
		if (value !== currentValueRef.current) {
			return;
		}
		if (!!value && (!selected || selected.id !== value)) {
			const index = list.findIndex((p) => {
				return p.id === value;
			});
			if (index !== -1) {
				setSelected(list[index]);
				onResolve?.(list[index]);
			} else {
				loadSelected();
			}
		}
	};

	const loadSelected = () => {
		const requestedValue = value;
		if (!requestedValue) {
			return;
		}
		API.getParticipant(requestedValue)
			.then((result) => {
				if (currentValueRef.current !== requestedValue) {
					return;
				}
				if (result) {
					// Сервер может вернуть основной хэш вместо запрошенного хэша пользователя
					setSelected({ ...result, id: requestedValue });
					onResolve?.(result);
				} else {
					if (preserveValueOnLookupFailure) {
						onLookupFailure?.();
						API.showToast(
							false,
							'Не удалось получить данные выбранного пользователя. Значение сохранено.',
						);
					} else if (!disabled) {
						setValue(undefined);
						console.warn(
							'Не удалось получить от сервера выбранного заявителя! Значение сброшено.',
						);
					}
					setSelected(undefined);
				}
			})
			.catch((e: any) => {
				console.warn(e);
			})
			.finally(() => {
				return setIsWait(false);
			});
	};

	const onSearchHandler = (text: string) => {
		if (paginationPageSize != null) {
			listRequestRef.current += 1;
			pageLoadingRef.current = false;
			setPage(0);
			setHasMore(false);
			setData([]);
			setIsWait(false);
		}
		return setSearch(text);
	};

	const onSelected = (participant: CustomerItemType) => {
		if (disabled) {
			return;
		}
		currentValueRef.current = participant.id;
		setValue(participant);
		setSelected(participant);
		if (paginationPageSize != null) {
			onSearchHandler('');
		}
		toggle();
	};

	const onClearHandler = () => {
		if (disabled) {
			return;
		}
		if (paginationPageSize != null && value) {
			currentValueRef.current = undefined;
			setValue(undefined);
			setSelected(undefined);
			onSearchHandler('');
		} else if (isOpened) {
			onSearchHandler('');
		} else {
			currentValueRef.current = undefined;
			setValue(undefined);
			setSelected(undefined);
		}
		setData([]);
	};

	// Подгружает следующую страницу, когда список прокручен до конца
	const onScrollList = () => {
		const list = listRef.current;
		if (
			paginationPageSize == null ||
			!hasMore ||
			pageLoadingRef.current ||
			!list
		) {
			return;
		}
		if (list.scrollHeight - list.scrollTop - list.clientHeight <= 24) {
			loadList(search, page + 1);
		}
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (!isOpened) {
			return;
		}
		switch (e.key) {
			case 'ArrowDown': {
				e.preventDefault();
				const next = Math.min(focusedIndex + 1, data.length - 1);
				setFocusedIndex(next);
				(
					listRef.current?.children[next] as HTMLElement | undefined
				)?.scrollIntoView({ block: 'nearest' });
				break;
			}
			case 'ArrowUp': {
				e.preventDefault();
				const next = Math.max(focusedIndex - 1, -1);
				setFocusedIndex(next);
				if (next >= 0) {
					(
						listRef.current?.children[next] as
							HTMLElement | undefined
					)?.scrollIntoView({ block: 'nearest' });
				}
				break;
			}
			case 'Enter':
				e.preventDefault();
				if (focusedIndex >= 0 && data[focusedIndex]) {
					onSelected(data[focusedIndex]);
				}
				break;
			case 'Escape':
				if (onOutsideClick) {
					onOutsideClick(e);
				} else {
					toggle();
				}
				break;
		}
	};

	const getSearchWarnText = () => {
		const length = minSearchLength - search.length;
		const result = {
			message: '',
			isEmpty: false,
		};
		if (isWait) {
			result.message = 'Ведется поиск...';
		} else if (minSearchLength <= 0) {
			result.message = '';
		} else if (length > 0) {
			result.message = `Введите минимум ${minSearchLength} символа`;
		} else if (!data.length) {
			result.message = 'По Вашему запросу ничего не найдено';
			result.isEmpty = true;
		}
		return result;
	};

	const renderOption = (item: CustomerItemType, index: number) => {
		return (
			<UserInfo
				key={'item_' + index}
				userName={item.text}
				userOrg={item.attrs[0]}
				email={item.email}
				className={clx(st.item, {
					[st.active]: item.id === value,
					[st.focused]: index === focusedIndex,
				})}
				onClick={() => {
					return onSelected(item);
				}}
			/>
		);
	};

	const renderMessage = () => {
		const { message, isEmpty } = getSearchWarnText();
		if (!message) {
			return null;
		}
		return (
			<p className={clx(st.message, { [st.error]: isEmpty })}>
				{message}
			</p>
		);
	};

	const renderBody = () => {
		return (
			<div ref={modalRef} className={st.customerListWrapper}>
				{renderMessage()}
				<ul
					ref={listRef}
					className={st.wrapper}
					onScroll={onScrollList}
				>
					{data.map(renderOption)}
				</ul>
			</div>
		);
	};

	const renderTb = () => {
		return (
			<div className={st.toolbar}>
				{!!isWait && <div className={st.spinner} />}
				{(showClearBtn || (isOpened && !!search)) && (
					<ClearBtn
						disable={
							disabled ||
							(paginationPageSize != null
								? !value && !search
								: isOpened
									? !search
									: !value)
						}
						onClick={onClearHandler}
					/>
				)}
			</div>
		);
	};

	return (
		<div ref={wrapperRef} {...otherProps} className={className}>
			<div
				className={clx(st.customerSelect, {
					[st.opened]: isOpened,
					[st.disabled]: disabled,
				})}
			>
				<SearchIcon />
				<input
					ref={inputRef}
					value={isOpened ? search : selectedName}
					placeholder={
						isOpened ? 'Введите ФИО для поиска' : placeholder
					}
					readOnly={disabled || !isOpened}
					onClick={() => {
						return !disabled && !isOpened && toggle();
					}}
					onChange={(e) => {
						return onSearchHandler(e.target.value);
					}}
					onKeyDown={onKeyDown}
				/>
				{renderTb()}
			</div>
			{isOpened && renderBody()}
		</div>
	);
};

export default ParticipantSelect;
